import { action, makeAutoObservable } from 'mobx'
import Web3 from 'web3'
import { IWeb3KeyProvider, IWeb3SendResult, Web3KeyProvider } from '@ankr.com/stakefi-web3'
import detectEthereumProvider from '@metamask/detect-provider'
import { Contract } from 'web3-eth-contract'
import axios from 'axios'
import { INFTCollection, collections } from './collections'
import { ChilizConfigProvider } from '../config/configProvider'
import { chains, IChainConfig } from '../config/chains'
import { ITokenMetadata, ITransferEvent } from '../utils/types'

const WHITELIST_ROLE = Web3.utils.keccak256('WHITELISTED')

export interface INFT {
  collectionName: string
  name: string
  address: string
  tokenId: number
  image: string
  description: string
  chain: string
  properties: any[]
  mintDate?: number
  maxSupply: number
  isRandom: boolean
}

export interface IUserCollections extends INFTCollection {
  nftsMinted: any[]
}

export interface IChainInfo {
  blockNumber: number
}

export class ChilizStore {
  public isConnected: boolean = false
  private readonly keyProvider: IWeb3KeyProvider
  private readonly web3Connection: Web3

  public featuredCollection: {maxMintPerAccount: number, supportsWhitelist: boolean, contract: Contract}
  public chainInfo: IChainInfo = {
    blockNumber: 1
  }

  public nftCollectionInfo: {[address: string]: any } = {}
  public nftCollectionContracts: {[address: string]: Contract} = {}
  public nftsLoaded: {[collectionAddress: string]: {[tokenId: string]: boolean}} = {}
  public userNfts: INFT[] = []
  public mintEvents: {[collectionAddress: string]: {events: any[], lastBlock: number}} = { }

  public constructor (readonly chilizConfigProvider: ChilizConfigProvider) {
    this.keyProvider = new Web3KeyProvider({})
    const defaultChainConfig = chilizConfigProvider.getChainConfig()
    const featuredCollection = collections[defaultChainConfig.chainId].filter(c => c.featured)[0]
    this.web3Connection = new Web3(defaultChainConfig.rpcUrl)
    this.featuredCollection = {
      maxMintPerAccount: Number(featuredCollection.maxMintPerAccount),
      supportsWhitelist: featuredCollection.supportsWhitelist,
      contract: new this.web3Connection.eth.Contract(featuredCollection.abi, featuredCollection.address)
    }
    makeAutoObservable(this)
  }

  public getKeyProvider (): IWeb3KeyProvider {
    return this.keyProvider
  }

  @action
  public async connectFromInjected (): Promise<void> {
    this.isConnected = false

    const provider: any = await detectEthereumProvider()
    if (typeof provider !== 'undefined' || provider !== null) {
      provider.on('chainChanged', (chainId: number) => {
        console.log('-- chain changed', chainId)
        window.location.reload()
      })
      provider.on('accountsChanged', async ([account]: string[]) => {
        window.location.reload()
      })
    }

    if (!this.keyProvider.isConnected()) {
      await this.keyProvider.connectFromInjected()
    }
    const chain = chains.find(chain => chain.chainId === this.keyProvider.currentChain())
    if (chain != null) {
      this.chilizConfigProvider.setConfigForChain(chain)
    } else return

    // Load user's nfts
    const account = this.keyProvider.currentAccount()
    for (const collection of collections[this.chilizConfigProvider.getChainConfig().chainId]) {
      this.nftCollectionContracts[collection.address] = this.keyProvider.createContract(collection.abi, collection.address)
      this.nftCollectionInfo[collection.address] = collection
      const contract = this.nftCollectionContracts[collection.address]
      if (collection.featured === true) {
        this.featuredCollection = {
          maxMintPerAccount: Number(collection.maxMintPerAccount),
          supportsWhitelist: collection.supportsWhitelist,
          contract
        }
      }
      contract.events
        .Transfer({ fromBlock: 0, filter: { to: account } })
        .on('data', async (e) => {
          const tokenId = e.returnValues.tokenId
          if (typeof this.nftsLoaded[collection.address] === 'undefined') {
            this.nftsLoaded[collection.address] = {}
          }
          if (this.nftsLoaded[collection.address][tokenId]) {
            return
          }
          this.nftsLoaded[collection.address][tokenId] = true

          const metadataURI: string = await contract.methods.tokenURI(tokenId).call()
          let metadata: INFT
          try {
            const { status, data } = await axios.get(metadataURI,
              {
                headers: { 'Content-Type': 'application/json' }
              })
            const blockData = await this.keyProvider.getWeb3().eth.getBlock(e.blockNumber)
            const [_, maxSupply] = await this.fetchSupplyInfo(collection.address)
            if (status === 200 && Boolean(data)) {
              metadata = {
                collectionName: collection.displayName,
                name: data.name,
                address: collection.address,
                tokenId,
                image: data.image.replace('ipfs://', 'https://chiliz.infura-ipfs.io/ipfs/'),
                description: data.description,
                chain: this.chilizConfigProvider.getConfig().chainConfig.chainName,
                properties: data.attributes,
                isRandom: collection.isRandom,
                mintDate: Number(blockData.timestamp),
                maxSupply
              }
              this.userNfts.push(metadata)
            }
          } catch (e) {
            console.log(' -- Error getting metadata', e)
          }
        })
    }

    this.isConnected = true
    try {
      this.chainInfo = await this.getChainInfo()
    } catch (e: any) {
      console.error(e)
    }
  }

  public async getBlockNumber (): Promise<number> {
    const blockNumber = await this.keyProvider.getWeb3().eth.getBlockNumber()
    return blockNumber
  }

  public async getChainInfo (): Promise<IChainInfo> {
    const blockNumber = await this.keyProvider.getWeb3().eth.getBlockNumber()
    this.chainInfo = {
      blockNumber
    } as any
    return this.chainInfo
  }

  public async addNetworkToMetaMask (chain: IChainConfig): Promise<void> {
    const currentChain = { ...this.chilizConfigProvider.getChainConfig() }
    this.chilizConfigProvider.setConfigForChain(chain)
    if (!this.keyProvider.isConnected()) {
      await this.keyProvider.connectFromInjected()
      this.isConnected = true
    }

    const web3 = this.keyProvider.getWeb3()
    const chainIdHex = `0x${this.chilizConfigProvider.getChainConfig().chainId.toString(16)}`
    try {
      await web3.givenProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }]
      })
    } catch (switchError: any) {
      if (switchError.code === 4902) {
        try {
          await web3.givenProvider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: chainIdHex,
                chainName: this.chilizConfigProvider.getChainConfig().chainName,
                rpcUrls: [this.chilizConfigProvider.getChainConfig().rpcUrl]
              }
            ]
          })
        } catch (addError: any) {
          console.error(addError)
          this.chilizConfigProvider.setConfigForChain(currentChain)
        }
      } else {
        this.chilizConfigProvider.setConfigForChain(currentChain)
        console.error(switchError)
      }
    }
  }

  public async disconnect (): Promise<void> {
    try {
      this.isConnected = false
      await this.keyProvider.disconnect()
    } catch (switchError: any) {
      console.error(switchError)
    }
  }

  public async getBalance (): Promise<string> {
    return await this.keyProvider.getWeb3().eth.getBalance(this.keyProvider.currentAccount())
  }

  public async mint (collectionAddress: string): Promise<IWeb3SendResult> {
    const contract = this.nftCollectionContracts[collectionAddress]
    const account = this.keyProvider.currentAccount()
    const data = contract.methods.mint(account, 1).encodeABI()
    const value = this.nftCollectionInfo[collectionAddress].isFreeToMint === false ? await contract.methods.salePrice().call() : '0'

    return await this.keyProvider.sendTransactionAsync(account, collectionAddress, {
      data,
      value,
      gasLimit: '300000',
      estimate: true
    })
  }

  public async fetchSupplyInfo (collectionAddress): Promise<[number, number]> {
    let total
    let max
    try {
      const contract = this.nftCollectionContracts[collectionAddress]
      try {
        total = await contract.methods.totalSupply().call()
      } catch (e) {
        total = 0
      }
      try {
        max = await contract.methods.maxSupply().call()
      } catch (e) {
        max = Infinity
      }
    } catch (e) {
      total = 0
      max = 0
    }
    return [total, max]
  }

  public async getTokenMetadata (collection, uri: string): Promise<any> {
    const metadata: ITokenMetadata = { name: '', tokenId: -1, image: '', description: '', properties: [] }
    try {
      const { status, data } = await axios.get(uri, { headers: { 'Content-Type': 'application/json' } })
      if (status === 200 && Boolean(data)) {
        metadata.name = data.name
        // The tokenId field must be consistent
        metadata.tokenId = typeof data.tokenId !== 'undefined' ? data.tokenId : data.edition
        metadata.image = data.image.replace('ipfs://', collection?.tokenImageDomain)
        metadata.description = data.description
        metadata.properties = data.attributes
      }
    } catch (e) {
      console.log(' -- Error getting metadata', e, uri)
    }
    return metadata
  }

  public async getTokenInfo (contract, collection, tokenId: number): Promise<ITokenMetadata> {
    const metadataURI = await contract.methods.tokenURI(tokenId).call()
    const metadata = await this.getTokenMetadata(collection, metadataURI.replace('ipfs://', collection?.tokenImageDomain)) as unknown as ITokenMetadata
    return metadata
  }

  public async getMintEvents (contract: any, collection?: INFTCollection, page: number = 1, perPage: number = 20, batchSize: number = 50_000): Promise<any> {
    if (collection === undefined) {
      return []
    }

    if (this.mintEvents[collection.address] === undefined) {
      this.mintEvents[collection.address] = { events: [], lastBlock: 0 }
    }

    const getEvents = async (f: number, t: number): Promise<ITransferEvent[]> => {
      return await contract.getPastEvents('Transfer', {
        fromBlock: f,
        toBlock: t ?? 'latest',
        filter: { from: '0x0000000000000000000000000000000000000000' }
      }) as unknown as ITransferEvent[]
    }

    const neededLength = perPage * (page - 1) + perPage
    // console.log('neededLength', neededLength, 'this.mintEvents[collection.address]', this.mintEvents[collection.address].events.length)
    if (this.mintEvents[collection.address].events.length < neededLength) {
      let from, to
      if (this.mintEvents[collection.address].lastBlock > 0) {
        to = this.mintEvents[collection.address].lastBlock - 1
        from = to - batchSize
      } else {
        to = collection.paginationToBlock ?? await this.getBlockNumber()
        from = to - batchSize
        from = from < (collection.paginationFromBlock ?? 0) ? (collection.paginationFromBlock ?? 0) : from
      }
      let fetchCursor = 0
      // console.log('Fetching events', from, to)
      while (fetchCursor <= perPage && from >= (collection.paginationFromBlock ?? 0)) {
        // console.log('cursor', fetchCursor)
        const transfers = await getEvents(from, to)
        this.mintEvents[collection.address].events.push(...transfers)
        fetchCursor += transfers.length
        this.mintEvents[collection.address].lastBlock = from
        to = from - 1
        from = (from - 1 - batchSize) < 0 ? 0 : from - 1 - batchSize
        // console.log('updated cursor', fetchCursor)
      }
    }
    // console.log('Total events found', this.mintEvents[collection.address].events.length)

    const fromIdx = perPage * (page - 1)
    const toIdx = fromIdx + perPage > this.mintEvents[collection.address].events.length ? this.mintEvents[collection.address].events.length : fromIdx + perPage
    // console.log('page, fromIdx, toIdx', page, fromIdx, toIdx)
    const mintEvents = this.mintEvents[collection.address].events.slice(fromIdx, toIdx)
    const hasMore = this.mintEvents[collection.address].lastBlock > (collection.paginationFromBlock ?? 0) || toIdx < this.mintEvents[collection.address].events.length
    // Fetch metadata for the range
    // console.log('Fetching metadata for', mintEvents.length, 'items')
    const items = await Promise.all(mintEvents.map(async ({ returnValues }) => {
      const tokenInfo = await this.getTokenInfo(contract, collection, returnValues.tokenId)
      return {
        tokenId: returnValues.tokenId,
        mintedTo: returnValues.to,
        image: tokenInfo.image,
        owner: await contract.methods.ownerOf(returnValues.tokenId).call() as string
      }
    }))

    return [items, hasMore]
  }

  public async getStep (): Promise<number> {
    // 0 = Before, 1 = PublicSale, 2 = SoldOut, 3 = Reveal
    let step
    try {
      try {
        step = await this.featuredCollection.contract.methods.step().call()
      } catch (error) {
        const [total, max] = await this.fetchSupplyInfo(this.featuredCollection.contract.options.address)
        step = total === max ? 2 : 1
      }
    } catch (error) {
      step = 0
    }
    return Number(step)
  }

  public async getNFTBalance (): Promise<number> {
    return this.featuredCollection.contract.methods.balanceOf(this.keyProvider.currentAccount()).call()
  }

  public async hasExceededMintLimit (): Promise<boolean> {
    if (!this.isConnected) {
      return false
    }
    const nftBalance = await this.getNFTBalance()
    return nftBalance >= this.featuredCollection.maxMintPerAccount
  }

  public async isUserWhitelisted (): Promise<boolean> {
    if (!this.isConnected || !this.featuredCollection.supportsWhitelist) {
      return true
    }
    const whitelistEnabled = await this.featuredCollection.contract.methods.isWhitelistEnabled().call() as boolean
    if (!whitelistEnabled) {
      return true
    }
    const role = this.web3Connection.eth.abi.encodeParameter('bytes32', WHITELIST_ROLE)
    return this.featuredCollection.contract.methods.hasRole(role, this.keyProvider.currentAccount()).call()
  }

  public async getNft (address: string, tokenId: number): Promise<INFT> {
    const collection = this.nftCollectionInfo[address]
    const contract = this.nftCollectionContracts[address]
    const tokenInfo = await this.getTokenInfo(contract, collection, tokenId)
    const metadata: INFT = {
      ...tokenInfo,
      collectionName: collection.displayName,
      address,
      chain: this.chilizConfigProvider.getConfig().chainConfig.chainName,
      isRandom: collection.isRandom,
      maxSupply: 0
    }
    try {
      const [_, maxSupply] = await this.fetchSupplyInfo(address)
      metadata.maxSupply = maxSupply
    } catch (e) {
      console.log(' -- Error getting metadata', e)
    }
    if (typeof metadata.tokenId === 'undefined') {
      metadata.tokenId = tokenId
    }
    return metadata
  }
}
